import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import {
  firstOrderPageQuery,
  remainingOrdersQuery,
} from '../../api/orderQueries'
import {
  formatPrice,
  getDisplayItemCount,
  getFirstProductImage,
  getOrderDisplayStatus,
  hasCustomMadeItems,
  hasMultipleProducts,
  mergeOrderPages,
  ORDERS_PAGE_SIZE,
} from '../../api/ordersApi'
import type { Environment } from '../../App'
import { useSkeletonPhase } from '../../hooks/useSkeletonPhase'
import { formatOrderDate, ORDER_DATE_LONG_FORMAT } from '../../utils/orderDate'
import { useDateLocale, useLabel } from '../../i18n'
import type { Order } from '../../types/order'
import { OrderNotice } from '../shared/OrderNotice'
import { OrderCard } from './OrderCard'
import OrderListSkeleton, { LIST_CONTAINER_CLASSES } from './OrderListSkeleton'

interface OrderListProps {
  accountNumber?: string
  env: Environment
  authToken?: string
  onOrderClick?: (orderId: string) => void
  onEmptyState?: (isEmpty: boolean) => void
  /**
   * The host already painted a placeholder for this list (the Customer Hub's
   * boot screen), so skip the anti-flash delay rather than blanking it.
   */
  skipSkeletonDelay?: boolean
}

export function OrderList({
  accountNumber = 'SF003696565', // Default account number for testing
  env,
  authToken,
  onOrderClick,
  onEmptyState,
  skipSkeletonDelay,
}: OrderListProps) {
  const t = useLabel()
  const dateLocale = useDateLocale()
  const formatListPrice = (amount: number, currency: string) => {
    const hasDecimals = Math.abs(amount - Math.trunc(amount)) > 1e-6
    return formatPrice(amount, currency, hasDecimals ? 2 : 0)
  }

  // Both queries come from `orderQueries` rather than being spelled here: the
  // Customer Hub prefetches the first page from its overview tab, and a key
  // written twice would leave that prefetch seeding an entry we never read.
  //
  // Painted as soon as it lands. Only this query gates the skeleton and the
  // error state — the rest of the list arrives underneath it.
  const {
    data: firstPage,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    ...firstOrderPageQuery({ accountNumber, env, authToken }),
    enabled: !!accountNumber,
  })
  const retryFirstPage = () => {
    void refetch()
  }

  // A short first page is the whole list, so there is nothing to follow up.
  const hasMorePages = (firstPage?.length ?? 0) >= ORDERS_PAGE_SIZE

  const { data: remainingOrders } = useQuery({
    ...remainingOrdersQuery({ accountNumber, env, authToken }),
    enabled: !!accountNumber && hasMorePages,
  })

  // Re-sorted on arrival, so a background page can reorder rows already on
  // screen. Accepted: seeing the newest orders sooner is worth the shuffle.
  //
  // The tail is gated on `hasMorePages` as well as fetched under it — otherwise
  // a refetch that shrinks the first page below a full page would keep merging
  // the previous tail out of the cache.
  const allOrders = useMemo(
    () =>
      mergeOrderPages(
        firstPage ?? [],
        hasMorePages ? (remainingOrders ?? []) : [],
      ),
    [firstPage, remainingOrders, hasMorePages],
  )

  const { phase, contentClassName } = useSkeletonPhase(isLoading, {
    skipDelay: skipSkeletonDelay,
  })

  // Notify parent about empty state (must be before conditional returns).
  // Keyed on the skeleton phase rather than `isLoading` so a shown skeleton
  // keeps its minimum visible time: the Customer Hub swaps this whole list out
  // for its own empty state on this callback, which would otherwise unmount the
  // skeleton mid-window (RP-5936). Mirrors the same guard in size-passport's
  // customer-passports-per-product.
  useEffect(() => {
    if (phase === 'content') {
      onEmptyState?.(allOrders.length === 0)
    }
  }, [phase, allOrders.length, onEmptyState])

  // Exact-layout row skeletons in the list's own container (invisible for
  // the first 150ms, so a fast/cached response never flashes them).
  if (phase !== 'content') {
    return <OrderListSkeleton isReserved={phase === 'reserve'} />
  }

  if (isError) {
    return (
      <OrderNotice
        title={t('orderList.error.listTitle')}
        body={t('orderList.error.listBody')}
        actionLabel={t('orderList.error.listAction')}
        onAction={retryFirstPage}
        isActionPending={isFetching}
      />
    )
  }

  if (allOrders.length === 0) {
    return (
      <div
        className="flex w-full items-center justify-center pt-40"
        data-testid="orders-empty"
      >
        <div className="text-lg text-gray-600">{t('orderList.list.empty')}</div>
      </div>
    )
  }

  return (
    <div
      className={`${LIST_CONTAINER_CLASSES}${
        contentClassName ? ` ${contentClassName}` : ''
      }`}
      data-testid="orders-list"
    >
      {allOrders.map((order: Order) => {
        const statusKey = getOrderDisplayStatus(order.maxFulfillmentStatusId)

        return (
          <OrderCard
            key={order.orderId}
            imageUrl={getFirstProductImage(order) ?? undefined}
            status={statusKey ? t(statusKey) : undefined}
            hasCustomMadeIcon={hasCustomMadeItems(order)}
            hasMultipleProducts={hasMultipleProducts(order)}
            title={t('orderList.list.orderedTitle', {
              date: formatOrderDate(
                order.orderDate,
                dateLocale,
                ORDER_DATE_LONG_FORMAT,
              ),
            })}
            orderNumber={order.orderId}
            itemCount={getDisplayItemCount(order)}
            price={formatListPrice(order.total, order.currencyCode)}
            accountNumber={accountNumber}
            onOrderClick={onOrderClick}
          />
        )
      })}
    </div>
  )
}
